/**
 * AIChatWidget.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Floating AI Chat Widget for FluxERP dashboard.
 *
 * Architecture:
 *   • Calls  POST /api/ai/chat  with ONLY the user's typed message.
 *   • Backend handles: intent extraction (LLM) → entity resolution → tool
 *     execution → formatting.  The frontend NEVER receives raw ERP data.
 *   • Write-action previews show a "Proceed" button that navigates the user
 *     to the correct module form — actual creation is NOT done via AI.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, {
  useState, useEffect, useRef, useCallback, memo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { aiAPI } from '../../services/api';
import './AIChatWidget.css';

// ─── Icons (inline SVG — no extra dep required) ──────────────────────────────
const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// ─── Loading phases displayed while the pipeline runs ────────────────────────
const LOADING_PHASES = [
  'Understanding request…',
  'Fetching data…',
  'Processing…',
];

// ─── Render a single formatted message (supports markdown-lite) ───────────────
const MessageBubble = memo(({ msg, onNavigate, onSelect, onCommit }) => {
  const isUser = msg.role === 'user';

  // Simple markdown renderer: bold, tables, bullet lists
  const renderText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let tableBuffer = [];
    let inTable = false;

    const flushTable = () => {
      if (tableBuffer.length < 2) {
        elements.push(...tableBuffer.map((l, i) => <p key={`tbl-p-${i}`}>{l}</p>));
        tableBuffer = [];
        inTable = false;
        return;
      }
      const headers = tableBuffer[0].split('|').filter(c => c.trim());
      const rows = tableBuffer.slice(2).map(r => r.split('|').filter(c => c.trim()));
      elements.push(
        <div key={`table-${elements.length}`} className="ai-table-wrap">
          <table className="ai-table">
            <thead>
              <tr>{headers.map((h, i) => <th key={i}>{h.trim()}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell.trim()}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableBuffer = [];
      inTable = false;
    };

    lines.forEach((line, i) => {
      if (line.startsWith('|')) {
        inTable = true;
        tableBuffer.push(line);
        return;
      }
      if (inTable) flushTable();

      // Bold: **text**
      const bold = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Bullet
      if (line.startsWith('• ') || line.startsWith('- ')) {
        elements.push(
          <li key={i} className="ai-list-item"
            dangerouslySetInnerHTML={{ __html: bold.substring(2) }} />
        );
        return;
      }
      // Heading: **...**  on its own line
      if (/^\*\*[^*]+\*\*$/.test(line.trim())) {
        elements.push(
          <p key={i} className="ai-heading"
            dangerouslySetInnerHTML={{ __html: bold }} />
        );
        return;
      }
      if (line.trim() === '') {
        elements.push(<br key={i} />);
        return;
      }
      elements.push(
        <p key={i} className="ai-line"
          dangerouslySetInnerHTML={{ __html: bold }} />
      );
    });
    if (inTable) flushTable();
    return elements;
  };

  if (isUser) {
    return (
      <div className="ai-msg ai-msg--user">
        <div className="ai-bubble ai-bubble--user">{msg.content}</div>
      </div>
    );
  }

  // Selection Card (Interactive Disambiguation)
  if (msg.unresolved && msg.unresolved.length > 0) {
    return (
      <div className="ai-msg ai-msg--bot">
        <div className="ai-avatar"><BotIcon /></div>
        <div className="ai-bubble ai-bubble--bot">
          <p className="ai-line">{msg.content}</p>
          <div className="ai-selection-area">
            {msg.unresolved.map((u, i) => (
              <div key={i} className="ai-selection-group">
                <p className="ai-selection-label">{u.message}</p>
                <div className="ai-choices">
                  {u.choices?.map(choice => (
                    <button 
                      key={choice.id} 
                      className="ai-choice-btn"
                      onClick={() => onSelect(`${u.type}:${choice.id}`)}
                    >
                      <span className="ai-choice-name">{choice.name}</span>
                      {choice.sku && <span className="ai-choice-meta">SKU: {choice.sku}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Workflow Results (Deterministic Graphs)
  if (msg.type === 'WORKFLOW_RESULT' && msg.results) {
    return (
      <div className="ai-msg ai-msg--bot">
        <div className="ai-avatar"><BotIcon /></div>
        <div className="ai-bubble ai-bubble--bot">
          <p className="ai-heading">📋 {msg.planDescription}</p>
          <div className="ai-workflow-steps">
            {msg.results.map((res, i) => (
              <div key={i} className="ai-workflow-step">
                <div className="ai-step-header">
                  <span className="ai-step-dot" />
                  <span className="ai-step-label">{res.label}</span>
                </div>
                {res.found ? (
                  <div className="ai-step-data">
                    {res.data?.totalOutstanding !== undefined && (
                      <p>Outstanding: <strong>${res.data.totalOutstanding.toLocaleString()}</strong></p>
                    )}
                    {res.data?.qty !== undefined && (
                      <p>Stock: <strong>{res.data.qty}</strong> {res.data.isLow ? '⚠️ Low Stock' : '✅'}</p>
                    )}
                  </div>
                ) : (
                  <p className="ai-step-error">No data found</p>
                )}
              </div>
            ))}
          </div>
          <p className="ai-line">{msg.content}</p>
        </div>
      </div>
    );
  }

  // Escalation to human support
  if (msg.type === 'escalation') {
    return (
      <div className="ai-msg ai-msg--bot">
        <div className="ai-avatar"><BotIcon /></div>
        <div className="ai-bubble ai-bubble--bot">
          <p className="ai-line">{msg.content}</p>
          <div className="ai-preview-actions">
            <button className="ai-btn ai-btn--primary" onClick={() => onNavigate(msg.navigateTo || '/crm/tickets')}>
              Open Support Ticket ↗
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Action preview (Write operations / Drafts)
  if (msg.preview) {
    return (
      <div className="ai-msg ai-msg--bot">
        <div className="ai-avatar"><BotIcon /></div>
        <div className="ai-bubble ai-bubble--bot ai-bubble--preview">
          <p className="ai-preview-label">⚠️ Governed AI Draft — Review Required</p>
          <div className="ai-preview-card">
            {Object.entries(msg.preview).map(([k, v]) => (
              <div key={k} className="ai-preview-row">
                <span className="ai-preview-key">{k}</span>
                <span className="ai-preview-val">{String(v)}</span>
              </div>
            ))}
          </div>
          <p className="ai-line">{msg.content}</p>
          <div className="ai-preview-actions">
            <button className="ai-btn ai-btn--primary" onClick={() => onCommit(msg.draftId)}>
              Confirm & Commit ✅
            </button>
            <button className="ai-btn ai-btn--ghost" onClick={() => onNavigate(msg.navigateTo)}>
              Edit Details Manualy ↗
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-msg ai-msg--bot">
      <div className="ai-avatar"><BotIcon /></div>
      <div className={`ai-bubble ai-bubble--bot ${msg.type === 'error' ? 'ai-bubble--error' : ''}`}>
        {renderText(msg.content)}
        {msg.navigateTo && (
          <button
            className="ai-btn ai-btn--ghost"
            onClick={() => onNavigate(msg.navigateTo)}
          >
            Yes, open it →
          </button>
        )}
      </div>
    </div>
  );
});

// ─── Typing indicator ─────────────────────────────────────────────────────────
const TypingIndicator = memo(({ phase }) => (
  <div className="ai-msg ai-msg--bot">
    <div className="ai-avatar"><BotIcon /></div>
    <div className="ai-bubble ai-bubble--bot ai-bubble--typing">
      <span className="ai-typing-dots">
        <span /><span /><span />
      </span>
      <span className="ai-typing-phase">{phase}</span>
    </div>
  </div>
));

// ─── Quick action chips ───────────────────────────────────────────────────────
const QuickActions = memo(({ actions, onSelect }) => (
  <div className="ai-quick-actions">
    {actions.map((a, i) => (
      <button key={i} className="ai-chip" onClick={() => onSelect(a.message)}>
        {a.label}
      </button>
    ))}
  </div>
));

// ─── Main widget ──────────────────────────────────────────────────────────────
const AIChatWidget = ({ publicMode = false }) => {
  const { user, isAuthenticated } = useAuth();
  const { baseCurrency } = useCurrency();
  const navigate = useNavigate();
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimised, setIsMinimised] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [quickActions, setQuickActions] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const phaseTimerRef = useRef(null);
  const sessionIdRef = useRef(crypto.randomUUID());

  // ── Load chat history & quick actions on mount ──────────────────────────
  useEffect(() => {
    if (publicMode) {
      setMessages([{
        id: 0,
        role: 'assistant',
        content: '👋 Welcome to FluxERP! I\'m your enterprise solutions expert.\n\nI can help you understand how FluxERP streamlines complex business operations, scales with your growth, and provides bank-grade security for your data.\n\nHow can I help you transform your business today?',
        type: 'welcome',
      }]);
      setQuickActions([
        { label: '🚀 Explore Features', message: 'What are the key enterprise features of FluxERP?' },
        { label: '💳 View Pricing', message: 'Show me the subscription plans.' },
        { label: '🔒 Data Security', message: 'How secure is my data on FluxERP?' },
        { label: '📅 Request a Demo', message: 'How can I get a personalized demo?' },
      ]);
      return;
    }

    if (!isAuthenticated) return;
    aiAPI.getHistory().then(res => {
      if (res.data?.history?.length) {
        const mapped = res.data.history.map((h, i) => ({
          id: i,
          role: h.role,
          content: h.content,
          type: h.type,
        }));
        setMessages(mapped);
      } else {
        // Welcome message
        setMessages([{
          id: 0,
          role: 'assistant',
        content: `👋 Hi${user?.firstName ? ` ${user.firstName}` : ''}! I'm your FluxERP assistant.\n\nI can help you:\n• Check customer or vendor balances\n• View invoices, purchases, or ledger entries\n• Check stock levels\n• Navigate to any module\n\nWhat would you like to do?`,
          type: 'welcome',
        }]);
      }
      if (res.data?.quickActions) setQuickActions(res.data.quickActions.slice(0, 4));
    }).catch(() => {
      setMessages([{
        id: 0, role: 'assistant',
        content: '👋 Hi! I\'m your FluxERP assistant. How can I help you today?',
        type: 'welcome',
      }]);
    });
  }, [isAuthenticated, user, publicMode]);

  // ── Auto-scroll to bottom on new messages ────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!isOpen && messages.length > 1) setHasUnread(true);
  }, [messages, isOpen]);

  // ── Loading phase cycling ─────────────────────────────────────────────────
  useEffect(() => {
    if (loading) {
      setLoadingPhase(0);
      phaseTimerRef.current = setInterval(() => {
        setLoadingPhase(p => (p + 1) % LOADING_PHASES.length);
      }, 2200);
    } else {
      clearInterval(phaseTimerRef.current);
    }
    return () => clearInterval(phaseTimerRef.current);
  }, [loading]);

  const openChat = () => {
    setIsOpen(true);
    setIsMinimised(false);
    setHasUnread(false);
    setTimeout(() => inputRef.current?.focus(), 150);
  };

  const handleNavigate = useCallback((path) => {
    navigate(path);
    setIsOpen(false);
  }, [navigate]);

  const clearHistory = useCallback(() => {
    setMessages([{
      id: 0, role: 'assistant',
      content: '🗑️ Chat cleared. How can I help you?',
      type: 'info',
    }]);
  }, []);

  const handleCommit = useCallback(async (draftId) => {
    setLoading(true);
    try {
      await aiAPI.commitDraft(draftId);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: '✅ Transaction committed successfully to the ledger.',
        type: 'success'
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: `⚠️ Commit failed: ${err.response?.data?.message || err.message}`,
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (text) => {
    const message = (text || input).trim();
    if (!message || loading) return;

    setInput('');
    const userMsg = { id: Date.now(), role: 'user', content: message };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const headers = { 
        'x-session-id': sessionIdRef.current,
        'x-currency': baseCurrency?.code || ''
      };
      const apiCall = publicMode ? aiAPI.publicChat(message) : aiAPI.chat(message, { headers });
      const res = await apiCall;
      const { response, meta } = res.data;

      // Update session ID if server provides a new one
      if (meta?.sessionId) sessionIdRef.current = meta.sessionId;

      const botMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.text || 'No response received.',
        type: response.type,
        data: response.data,
        preview: response.preview,
        unresolved: response.unresolved,
        navigateTo: response.navigateTo,
        action: response.action,
        results: response.results,
        planDescription: response.planDescription
      };
      setMessages(prev => [...prev, botMsg]);

    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: `⚠️ ${errMsg}`,
        type: 'error',
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't render at all if not authenticated AND not in public mode
  if (!isAuthenticated && !publicMode) return null;

  // Derive dynamic theme variables from MUI
  const themeStyle = {
    '--ai-primary': theme.palette.primary.main,
    '--ai-primary-dark': theme.palette.primary.dark || '#4f46e5',
    '--ai-primary-contrast': theme.palette.primary.contrastText || '#fff',
    '--ai-primary-glow': `${theme.palette.primary.main}40`,
    '--ai-user-bg': `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light || '#818cf8'})`,
  };

  return (
    <div style={themeStyle}>
      {/* ── Floating trigger button ─────────────────────────────────────── */}
      {!isOpen && (
        <button
          id="ai-chat-trigger"
          className="ai-trigger"
          onClick={openChat}
          title="Open AI Assistant"
          aria-label="Open FluxERP AI Assistant"
        >
          <BotIcon />
          {hasUnread && <span className="ai-unread-badge" />}
        </button>
      )}

      {/* ── Chat panel ─────────────────────────────────────────────────── */}
      {isOpen && (
        <div className={`ai-panel ${isMinimised ? 'ai-panel--minimised' : ''}`}
          role="dialog" aria-label="FluxERP AI Assistant">
          {/* Header */}
          <div className="ai-header">
            <div className="ai-header-left">
              <div className="ai-header-icon"><BotIcon /></div>
              <div>
                <div className="ai-header-title">
                  FluxERP Assistant
                  {publicMode && <span className="ai-header-badge">Enterprise</span>}
                </div>
              </div>
            </div>
            <div className="ai-header-actions">
              <button className="ai-icon-btn" onClick={clearHistory} title="Clear chat" aria-label="Clear chat history">
                <TrashIcon />
              </button>
              <button className="ai-icon-btn" onClick={() => setIsMinimised(m => !m)}
                title={isMinimised ? 'Expand' : 'Minimise'} aria-label={isMinimised ? 'Expand chat' : 'Minimise chat'}>
                <ChevronDown style={{ transform: isMinimised ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              <button className="ai-icon-btn ai-icon-btn--close" onClick={() => setIsOpen(false)}
                title="Close" aria-label="Close AI Assistant">
                <CloseIcon />
              </button>
            </div>
          </div>

          {!isMinimised && (
            <>
              {/* Disclaimer banner */}
              <div className="ai-disclaimer">
                🔒 Your data never leaves FluxERP. AI is used only for intent recognition.
              </div>

              {/* Messages */}
              <div className="ai-messages" role="log" aria-live="polite">
                {messages.map(msg => (
                  <MessageBubble 
                    key={msg.id} 
                    msg={msg} 
                    onNavigate={handleNavigate} 
                    onSelect={sendMessage}
                    onCommit={handleCommit}
                  />
                ))}
                {loading && (
                  <TypingIndicator phase={LOADING_PHASES[loadingPhase]} />
                )}
                <div ref={bottomRef} />
              </div>

              {/* Quick actions (shown when chat is idle) */}
              {!loading && quickActions.length > 0 && messages.length <= 2 && (
                <QuickActions actions={quickActions} onSelect={sendMessage} />
              )}

              {/* Input area */}
              <div className="ai-input-area">
                <textarea
                  ref={inputRef}
                  id="ai-chat-input"
                  className="ai-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about customers, invoices, stock…"
                  rows={1}
                  maxLength={500}
                  disabled={loading}
                  aria-label="Type your message"
                />
                <button
                  id="ai-chat-send"
                  className={`ai-send-btn ${(!input.trim() || loading) ? 'ai-send-btn--disabled' : ''}`}
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  aria-label="Send message"
                >
                  <SendIcon />
                </button>
              </div>
              <div className="ai-char-count">{input.length}/500</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIChatWidget;
